import { useEffect, useMemo, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn, DataTable, TEIQspinner } from '../ui';
import type { DataTableColumn } from '../ui';
import type { BriefingTopic } from './dailyBriefingData';
import { listImportableResources } from './dailyBriefingData';
import { StyleBadge } from './TodaysBriefingCard';

interface ImportFromResourcesModalProps {
    visible: boolean;
    companyId: string;
    saving: boolean;
    onDismiss: () => void;
    onImport: (topics: BriefingTopic[]) => Promise<void>;
}

export function ImportFromResourcesModal({
    visible,
    companyId,
    saving,
    onDismiss,
    onImport,
}: ImportFromResourcesModalProps) {
    const [loading, setLoading] = useState(false);
    const [resources, setResources] = useState<BriefingTopic[]>([]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        if (!visible || !companyId) return;

        setLoading(true);
        setSelected({});
        (async () => {
            try {
                const items = await listImportableResources(companyId);
                if (!cancelled) setResources(items);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [visible, companyId]);

    const columns = useMemo<DataTableColumn<BriefingTopic>[]>(
        () => [
            {
                id: 'select',
                header: 'Select',
                thClassName: 'visually-hidden',
                renderCell: (row) => (
                    <input
                        type="checkbox"
                        checked={Boolean(selected[row.id])}
                        onChange={(e) =>
                            setSelected((prev) => ({ ...prev, [row.id]: e.target.checked }))
                        }
                        aria-label={`Import ${row.title}`}
                    />
                ),
            },
            {
                id: 'style',
                header: 'Style',
                renderCell: (row) => <StyleBadge style={row.style} />,
            },
            {
                id: 'topic',
                header: 'Resource',
                mobileCardPrimary: true,
                renderCell: (row) => row.title,
            },
        ],
        [selected],
    );

    const selectedTopics = resources.filter((r) => selected[r.id]);

    return (
        <CustomModal
            visible={visible}
            onDismiss={onDismiss}
            header="Import from Resources"
            size="large"
            footer={
                <div className="db-modal-footer-actions">
                    <Btn variant="secondary" buttonText="Cancel" buttonOnClick={onDismiss} buttonDisabled={saving} />
                    <Btn
                        variant="primary"
                        buttonText={
                            saving
                                ? 'Importing…'
                                : `Import${selectedTopics.length ? ` (${selectedTopics.length})` : ''}`
                        }
                        buttonOnClick={() => onImport(selectedTopics)}
                        buttonDisabled={saving || selectedTopics.length === 0}
                    />
                </div>
            }
        >
            {loading ? (
                <div className="db-modal-loading">
                    <TEIQspinner />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    rows={resources}
                    getRowKey={(row) => row.id}
                    emptyState={
                        <div className="db-empty-state">
                            No importable documents or videos found. Upload files in Resources first.
                        </div>
                    }
                    mobileCardLayout
                />
            )}
        </CustomModal>
    );
}
